import type {PosterAnimationGenerationParams} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {AiVideoAudio, AiVideoQuality} from '@Panels/ai-video-panel/ai-video-panel.types';
import type {AiAnimationSourceType} from '@Libraries/ai-poster-animation.library';

export interface AiPosterAnimationPanelProps {
  onAnimationAddedToDesign?: () => void;
  isModal?: boolean;
  onClose?: () => void;
  isPromptMode?: boolean;
  onPromptModeChange?: (isPromptMode: boolean) => void;
  sourceType?: AiAnimationSourceType;
}

export interface PosterAnimationSourceCapture {
  imageData: string;
  width: number;
  height: number;
}

export const DEFAULT_POSTER_ANIMATION_QUALITY = AiVideoQuality.HD;

export const DEFAULT_POSTER_ANIMATION_AUDIO = AiVideoAudio.NO;

export const POSTER_ANIMATION_OVERLAY_ASSET = 'turn-into-video-panel-preview.webm';

export const ITEM_ANIMATION_OVERLAY_ASSET = 'animate-item-with-ai-panel-preview-v2.webm';

/** One entity per source, like the grids: a draft typed in the page panel must not surface in an item's. */
/** Long edge of the still sent to Veo, for both a whole-page and a single-item snapshot. */
export const ANIMATION_SNAPSHOT_LONG_EDGE = 1080;

export const getPosterAnimationPromptBoxId = (sourceType: AiAnimationSourceType): string => `poster-animation-prompt-box-${sourceType}`;

export const POSTER_ANIMATION_SKELETON_ID_PREFIX = 'poster-animation-loading-skeleton-';

export const POSTER_ANIMATION_GENERATION_LOADING_KEY = 'poster-animation-generation';

export const POSTER_ANIMATION_READY_LOADING_KEY = 'poster-animation-ready';

export const POSTER_ANIMATION_READY_TOAST_DURATION_MS = 10000;

export const PROMPT_BOX_LOADING_RESET_MS = 1000;

export const MAX_CONCURRENT_POSTER_ANIMATIONS = 10;

/** Floor for a rejoined poll, so a job recorded just short of its deadline still gets one look. */
export const POSTER_ANIMATION_MIN_REJOIN_POLL_MS = 30_000;

/** How long to wait for a replaced item's video to register on its page before giving up on autoplay. */
export const ANIMATION_PLAYABLE_TIMEOUT_MS = 8_000;

/** Gap between checks while waiting for that video to register. */
export const ANIMATION_PLAYABLE_POLL_MS = 100;

/** What it takes to draw a generation as in progress, whether it was just started or just rejoined. */
export interface PosterAnimationInProgress {
  id: string;
  width: number;
  height: number;
  params: PosterAnimationGenerationParams;
}

export const HIDE_FINALIZE_HINT_AT_GENERATION_COUNT = 5;

/**
 * Tooltips inside the panel and the results grid render behind them without this: both sit in their
 * own stacking context, so popper's default z-index loses. Shared by every tooltip in the panel.
 */
export const PANEL_TOOLTIP_POPPER_CONFIG = {
  modifiers: [
    {
      name: 'zIndexFix',
      enabled: true,
      phase: 'write' as const,
      fn: ({state}: {state: {elements: {popper: HTMLElement}}}): void => {
        state.elements.popper.style.zIndex = '5000';
      },
    },
  ],
};
